/**
 * Per-tenant auth sessions (token + refresh) kept in localStorage.
 * Super admin holds one session per logged-in tenant; everyone else holds one.
 * Module-agnostic — no carbon-credit knowledge here.
 */
import {
  getPrimaryTenantKey,
  normalizeAdminUrl,
  normalizeApiBase,
  tenantKeyFromAdminUrl,
} from '@/utils/tenant/tenants';

const TENANT_SESSIONS_KEY = 'CF_TENANT_SESSIONS';
const LEGACY_SECONDARY_KEY = 'CF_SECONDARY_TOKENS';

function readLegacySecondaryTokens() {
  try {
    return JSON.parse(localStorage.getItem(LEGACY_SECONDARY_KEY) || '{}');
  } catch {
    return {};
  }
}

function readTenantSessions() {
  try {
    const raw = localStorage.getItem(TENANT_SESSIONS_KEY);
    if (raw) {
      return JSON.parse(raw);
    }
  } catch {
    // fall through to legacy migration
  }

  const legacy = readLegacySecondaryTokens();
  const sessions = {};
  const primaryKey = getPrimaryTenantKey();
  const primaryToken = localStorage.getItem('token');
  const primaryRefresh = localStorage.getItem('refreshtoken');
  const primaryAdmin = normalizeAdminUrl(
    localStorage.getItem('CF_BASE_URL') || process.env.VUE_APP_BASE_URL || ''
  );

  if (primaryAdmin && primaryToken) {
    sessions[primaryKey] = {
      adminUrl: primaryAdmin,
      token: primaryToken,
      refreshtoken: primaryRefresh,
    };
  }

  Object.entries(legacy).forEach(([adminUrl, tokens]) => {
    const key = tenantKeyFromAdminUrl(adminUrl);
    if (!sessions[key]?.token && tokens?.token) {
      sessions[key] = {
        adminUrl: normalizeAdminUrl(adminUrl),
        token: tokens.token,
        refreshtoken: tokens.refreshtoken,
      };
    }
  });

  return sessions;
}

function writeTenantSessions(sessions) {
  localStorage.setItem(TENANT_SESSIONS_KEY, JSON.stringify(sessions));
}

export function setTenantSession(tenantKey, adminUrl, token, refreshtoken) {
  if (!tenantKey || !token) return;
  const sessions = readTenantSessions();
  sessions[tenantKey] = {
    adminUrl: normalizeAdminUrl(adminUrl),
    token,
    refreshtoken,
  };
  writeTenantSessions(sessions);
}

export function clearTenantSessions() {
  localStorage.removeItem(TENANT_SESSIONS_KEY);
  localStorage.removeItem(LEGACY_SECONDARY_KEY);
}

export function getLoggedInTenantKeys() {
  return Object.entries(readTenantSessions())
    .filter(([, session]) => session?.token)
    .map(([key]) => key);
}

export function getOauthTokenForApiBase(apiBaseUrl) {
  const targetAdmin = `${normalizeApiBase(apiBaseUrl)}/admin`;
  const sessions = readTenantSessions();
  const hit = Object.values(sessions).find(
    (session) => normalizeAdminUrl(session.adminUrl) === normalizeAdminUrl(targetAdmin)
  );
  if (hit?.token) {
    return hit.token;
  }
  if (normalizeAdminUrl(targetAdmin) === normalizeAdminUrl(
    localStorage.getItem('CF_BASE_URL') || process.env.VUE_APP_BASE_URL || ''
  )) {
    return localStorage.getItem('token');
  }
  return null;
}

export function getOauthTokenForRequestUrl(requestUrl) {
  if (!requestUrl || !requestUrl.startsWith('http')) {
    return null;
  }
  try {
    const url = new URL(requestUrl);
    if (!url.pathname.startsWith('/api')) {
      return null;
    }
    return getOauthTokenForApiBase(`${url.origin}/api`);
  } catch {
    return null;
  }
}
